import {handicaddieAsyncThunk} from "../../asyncThunk";
import {
    handleSetLoadingReviewFeed,
    handleSetReviewFeedErrMsg,
    LoadedCaddiesPayload,
    LoadedReviewsPayload,
    LoadedStatsPayload
} from "./slice";
import {
    PageableReviewResponse,
    ReviewSearchRequest,
    ReviewSearchStatisticsRequest,
    ReviewService
} from "../../../api/ReviewService";
import {ClubCaddieService} from "../../../api/ClubCaddieService";
import {RootState} from "../../store";

const DEFAULT_PAGE_SIZE = 5;

export const loadReviews = handicaddieAsyncThunk("Reviews/loadReviews", async (_, thunkAPI): Promise<LoadedReviewsPayload> => {
    let selectedClub = thunkAPI.getState().clubs.selectedClub

    if (!selectedClub) {
        return {
            errMsg: "Current club could not be detected. Please refresh the page."
        };
    }

    let resp = await ReviewService().searchReviews(buildSearchPayload(selectedClub.id, 0, thunkAPI.getState()))
    thunkAPI.dispatch(handleSetLoadingReviewFeed(false));

    return handleResponse(resp);
});

export const loadNextPageReviews = handicaddieAsyncThunk("Reviews/loadNextPageReviews", async (_, thunkAPI): Promise<LoadedReviewsPayload> => {
    let selectedClub = thunkAPI.getState().clubs.selectedClub
    let atMax = thunkAPI.getState().review.reviewFeed.atMax
    let loading = thunkAPI.getState().review.reviewFeed.loading

    if (!selectedClub) {
        return {
            errMsg: "Current club could not be detected. Please refresh the page."
        };
    }

    if (atMax || loading) {
        return {}
    }

    thunkAPI.dispatch(handleSetReviewFeedErrMsg(undefined));
    thunkAPI.dispatch(handleSetLoadingReviewFeed(true));

    let page = thunkAPI.getState().review.reviewFeed.nextPage

    let resp = await ReviewService().searchReviews(buildSearchPayload(selectedClub.id, page, thunkAPI.getState()))
    setTimeout(() => thunkAPI.dispatch(handleSetLoadingReviewFeed(false)), 300);
    return handleResponse(resp);
});

export const loadStatistics = handicaddieAsyncThunk("Reviews/loadStatistics", async (_, thunkAPI): Promise<LoadedStatsPayload> => {
    let selectedClub = thunkAPI.getState().clubs.selectedClub

    if (!selectedClub) {
        return {
            errMsg: "Unable to detect selected club. Please refresh to try again."
        }
    }

    let resp = await ReviewService().searchStatistics(buildStatisticsPayload(selectedClub.id, thunkAPI.getState()))
    if (resp.error) {
        return {
            errMsg: resp.error.message
        }
    }

    return {
        result: resp.statistics
    }
});

export const loadClubCaddies = handicaddieAsyncThunk("Reviews/loadClubCaddies", async (_, thunkAPI): Promise<LoadedCaddiesPayload> => {
    let selectedClub = thunkAPI.getState().clubs.selectedClub

    if (!selectedClub) {
        return {
            errMsg: "Current club could not be detected. Please refresh the page.",
            caddies: [],
        };
    }

    let resp = await ClubCaddieService().getAllCaddies(selectedClub.id)
    if (resp.error) {
        return {
            errMsg: resp.error.message,
            caddies: []
        }
    }

    return {
        caddies: resp.caddies ?? []
    }
});

const handleResponse = (resp: PageableReviewResponse) => {
    if (resp.error) {
        return {
            errMsg: resp.error.message
        };
    }

    if (!resp.page) {
        return {
            errMsg: "Unable to retrieve the reviews. Please refresh the page."
        }
    }

    return {
        result: {
            reviews: resp.page.resource,
            page: resp.page.page + 1,
            atMax: resp.page.pageCount === 0 || resp.page.page === (resp.page.pageCount - 1)
        }
    };
}

const buildSearchPayload = (clubId: string, page: number, state: RootState): ReviewSearchRequest => {
    let statsRequest = buildStatisticsPayload(clubId, state);

    return {
        ...statsRequest,
        page: page,
        size: DEFAULT_PAGE_SIZE
    }
}

const buildStatisticsPayload = (clubId: string, state: RootState): ReviewSearchStatisticsRequest => {
    let fromDate = state.review.filters.date.from
    let toDate = state.review.filters.date.to

    let stars = undefined
    if (state.review.filters.stars.length > 0) {
        stars = state.review.filters.stars
    }

    let caddies = undefined
    if (state.review.filters.caddie.length > 0) {
        caddies = state.review.filters.caddie.map(caddie => caddie.caddieId)
    }

    return {
        clubId,
        from: fromDate,
        to: toDate,
        starRating: stars,
        caddieIds: caddies
    }
}